import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import isEqual from 'lodash/isEqual'
import { ActivityState } from '@/api/models/activity'
import { type LearnerObjectiveAccessFullModel, ObjectiveWorkflowState } from '@/api/models/aggregate'
import { Attention } from '@/components/Attention/Attention'
import { useSidebarLayers } from '@/features/layout/hooks/useSidebarLayers'
import { useIsActivitySupportsMobileReview } from '@/hooks/useIsActivitySupportsMobileReview'
import { useIsCatalogCourse } from '@/hooks/useIsCatalogCourse'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useMemoTranslation } from '@/hooks/useMemoTranslation'
import { useAppDispatch } from '@/store/hooks'
import { setLastPageURL } from '@/store/slices/layoutSlice'
import { ButtonContainer } from './components/Buttons/ButtonContainer/ButtonContainer'
import { ShareButton } from './components/Buttons/ShareButton/ShareButton'
import { CatalogCourseButtons } from './components/CatalogCourseButtons/CatalogCourseButtons'
import { EnrolledCourseButtons } from './components/EnrolledCourseButtons/EnrolledCourseButtons'
import styles from './CourseInfo.module.scss'
import { COURSE_CONTENT_SLUG } from '../ActivityTypes/hooks/useSetCourseSidebarLayers'
import { useIsNotAvailable } from '../hooks/useIsNotAvailable'

type CourseInfoProps = {
  aggregate: LearnerObjectiveAccessFullModel
  onStartWorkflow: (isRetake?: boolean, owaId?: string) => Promise<unknown>
  hasResults: boolean
  children?: ReactNode
  supportReview?: boolean
}

export const CourseInfo: FC<CourseInfoProps> = ({
  aggregate,
  onStartWorkflow,
  hasResults,
  children,
  supportReview,
}) => {
  const { t, i18n } = useMemoTranslation()
  const dispatch = useAppDispatch()

  const isMobile = useIsMobile()
  const supportsMobileReview = useIsActivitySupportsMobileReview()
  const isCatalog = useIsCatalogCourse()

  const { sidebarLayers, setLayers } = useSidebarLayers()
  const { isNotAvailable, isNotAvailableMessage, canReview, isProctorSessionLoading } = useIsNotAvailable(aggregate)

  const [starting, setStarting] = useState(false)

  const { objective } = aggregate || {}

  const startWorkflowHandler = useCallback(
    async (isRetake = false, owaId?: string) => {
      setStarting(true)

      dispatch(setLastPageURL(''))

      try {
        await onStartWorkflow(isRetake, owaId)
      } finally {
        setStarting(false)
      }
    },
    [dispatch, onStartWorkflow],
  )

  const isDraftOrArchived = useMemo(
    () => [ActivityState.draft, ActivityState.archived].includes(objective?.activity?.state),
    [objective?.activity?.state],
  )

  const mobileUnsupported = isMobile && !supportsMobileReview
  const shouldHideContent = isNotAvailable && !mobileUnsupported

  useEffect(() => {
    if (!shouldHideContent) return

    const filteredLayers = (sidebarLayers || []).filter(
      (layer) => typeof layer === 'object' && layer.slug !== COURSE_CONTENT_SLUG,
    )

    if (!isEqual(filteredLayers, sidebarLayers)) {
      setLayers(filteredLayers)
    }
  }, [shouldHideContent, sidebarLayers, setLayers])

  const statusAlerts = useMemo(
    () => (
      <>
        {objective?.activity?.state === ActivityState.draft && <Attention message={t('comingSoon')} />}
        {objective?.activity?.state === ActivityState.archived && <Attention message={t('notAvailableCourse')} />}
      </>
    ),
    [t, objective?.activity?.state],
  )

  const notAvailableWarning = useMemo(() => {
    const isInProgress =
      aggregate?.objectiveWorkflowAggregate?.lastObjectiveWorkflow?.state === ObjectiveWorkflowState.inProgress

    if (!isNotAvailable || isProctorSessionLoading || isDraftOrArchived) return null
    if (isInProgress && canReview) return null

    return <Attention message={isNotAvailableMessage} />
  }, [isNotAvailable, isProctorSessionLoading, isNotAvailableMessage, isDraftOrArchived, canReview, aggregate])

  const shareButton = useMemo(() => {
    if (!isDraftOrArchived) return null

    return (
      <div className={styles.actions}>
        <ButtonContainer>
          <ShareButton />
        </ButtonContainer>
      </div>
    )
  }, [isDraftOrArchived])

  const courseButtons = useMemo(() => {
    if (isDraftOrArchived) return statusAlerts

    return isCatalog ? (
      <CatalogCourseButtons
        disabled={Boolean(isNotAvailableMessage)}
        loading={starting}
        startWorkflow={startWorkflowHandler}
      />
    ) : (
      <EnrolledCourseButtons
        aggregate={aggregate}
        hasResults={hasResults}
        supportReview={supportReview}
        startWorkflow={startWorkflowHandler}
        disabled={Boolean(isNotAvailableMessage)}
        isWorkflowStarting={starting}
        canReview={canReview}
      />
    )
  }, [
    isDraftOrArchived,
    isCatalog,
    isNotAvailableMessage,
    starting,
    startWorkflowHandler,
    aggregate,
    hasResults,
    supportReview,
    statusAlerts,
    canReview,
  ])

  return (
    <div className={styles.data}>
      <h1 className={styles.title} dir={i18n.dir() === 'rtl' ? 'rtl' : 'auto'}>
        {objective.name}
      </h1>
      {children}
      {courseButtons}
      {notAvailableWarning}
      {shareButton}
    </div>
  )
}
